import React from "react";

import type { BookkeepingType, ClientId } from "../../domain/types";
import { wantsBooksConnection } from "../../domain/steps";
import { BOOKKEEPING_TYPE_LABEL, TALLY_BOOKKEEPING_TYPE_LABEL } from "../../domain/constants";
import { SHOW_CATCHUP_BOOKKEEPING } from "../../domain/flags";
import { getAnswer, persistAnswer, updateClient, useCoClient } from "../../data/store";
import {
  CATCH_UP_TRAINING_ANSWER_KEY,
  type CatchUpTrainingAnswer,
} from "./CatchUpTrainingStep";
import { Callout } from "../../components/Callout";
import { DecisionCardOption } from "../../components/DecisionCards";
import { QuestionCard } from "../../components/QuestionCard";
import { SetupChecklist, SetupChecklistItem } from "../../components/SetupChecklist";
import { SubStepShell } from "../../components/SubStepShell";
import { cn } from "../../components/cn";
import { CheckIcon } from "../../components/icons";
import { useSubStepNav } from "../useSubStepNav";

const BOOKKEEPING_TYPE_OPTIONS: DecisionCardOption[] = [
  {
    value: "existing-zoho-current",
    title: BOOKKEEPING_TYPE_LABEL["existing-zoho-current"],
    description:
      "Keep this client's current organisation as-is. We'll set an accounting start date and begin from there.",
  },
  {
    value: "new-zoho-opening-balances",
    title: BOOKKEEPING_TYPE_LABEL["new-zoho-opening-balances"],
    description: "Start a new organisation and bring over opening balances from Tally, Excel, or old Zoho Books.",
  },
  {
    value: "new-company",
    title: BOOKKEEPING_TYPE_LABEL["new-company"],
    description: "No prior books. We'll set a start date and begin fresh.",
    consequence: "We'll set an account start date and begin from there.",
  },
  {
    value: "catch-up",
    title: BOOKKEEPING_TYPE_LABEL["catch-up"],
    description: "Books are behind. We'll treat this like a fresh start from a previous-year start date.",
  },
];

const TALLY_BOOKKEEPING_TYPE_OPTIONS: DecisionCardOption[] = [
  {
    value: "existing-zoho-current",
    title: TALLY_BOOKKEEPING_TYPE_LABEL["existing-zoho-current"],
    description:
      "Keep this client's current company as-is. We'll set an accounting start date and begin from there.",
  },
  {
    value: "new-zoho-opening-balances",
    title: TALLY_BOOKKEEPING_TYPE_LABEL["new-zoho-opening-balances"],
    description:
      "Start a new company in TallyPrime with this client's name. You can also bring in opening balances. Zournal imports those.",
  },
  // Hidden, not removed. TallyPrime cannot create a company from an XML import,
  // so this card is not offered. Uncomment to show it again.
  // {
  //   value: "new-company",
  //   title: TALLY_BOOKKEEPING_TYPE_LABEL["new-company"],
  //   description: "No prior books. We'll set a start date and begin fresh.",
  //   consequence: "We'll set an account start date and begin from there.",
  // },
  {
    value: "catch-up",
    title: TALLY_BOOKKEEPING_TYPE_LABEL["catch-up"],
    description: "Books are behind. We'll treat this like a fresh start from a previous-year start date.",
  },
];

// Informational, not a gate: these are the points of the route from old books
// to a new organisation, shown so the CA knows what's ahead. Most of them
// happen on later screens (or in Zoho), so Continue doesn't wait on them.
const NEW_BOOKS_CHECKLIST: SetupChecklistItem[] = [
  {
    id: "create-org",
    label: "Create a new Zoho Books organisation",
    hint: "The CA does this in Zoho directly. Zournal doesn't create organisations for you.",
  },
  {
    id: "export-masters",
    label: "Export masters from the old books",
    hint: "Chart of accounts, customers, vendors, and items.",
  },
  {
    id: "import-masters",
    label: "Import masters into the new organisation",
    hint: "Upload the exported masters into the new Zoho organisation.",
  },
  {
    id: "set-start-date",
    label: "Set the books start date / opening balances cutoff",
    hint: "Decide the date current balances will be entered as of.",
  },
  {
    id: "tax-opening-balances",
    label: "Set tax opening balances in Zournal",
    hint: "We'll push them to Zoho for you.",
  },
  {
    id: "connect-zournal",
    label: "Connect Zournal to the new organisation",
    hint: "You'll do this in the next step, once the organisation is ready.",
  },
];

function companyNameHint(companyName: string): string {
  const name = companyName.trim();
  if (name === "") {
    return "The company name must match the Company Legal Name from Company details.";
  }
  return `The company name must match ${name}.`;
}

// The opening-balance route the CA follows in Tally Prime. A completely new
// company is created by the connector, so it has no list here.
function tallySetupChecklist(companyName: string): SetupChecklistItem[] {
  return [
    {
      id: "create-company",
      label: "Create a new company in Tally Prime",
      hint: companyNameHint(companyName),
    },
    {
      id: "export-masters",
      label: "Export masters from the old Tally company",
      hint: "Chart of accounts, customers, vendors, and items.",
    },
    {
      id: "import-masters",
      label: "Import masters into the new Tally company",
    },
    {
      id: "skip-banks",
      label: "Leave bank and credit card accounts out of the import",
      hint: "We'll create those later in onboarding.",
    },
    {
      id: "connect-zournal",
      label: "Connect Zournal to this Tally company",
      hint: "You'll do this in the next step, once the company is ready.",
    },
  ];
}

// A previous-year date in the YYYY-MM-DD shape the start-date input expects —
// catch-up's default, still editable on the next screen.
function priorYearDate(): string {
  const now = new Date();
  now.setFullYear(now.getFullYear() - 1);
  return now.toISOString().slice(0, 10);
}

function BackendCard({
  title,
  description,
  selected,
  onSelect,
}: {
  title: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex flex-col gap-2 rounded-lg border p-4 text-left transition-colors",
        selected ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-medium">{title}</span>
        <span
          className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded-full border",
            selected ? "border-primary bg-primary text-primary-foreground" : "border-border"
          )}
        >
          {selected && <CheckIcon className="size-3" />}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </button>
  );
}

function BackendChoice({
  system,
  onSelectZoho,
  onSelectTally,
}: {
  system: string;
  onSelectZoho: () => void;
  onSelectTally: () => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <BackendCard
        title="Zoho Books"
        description="Zournal connects directly and proposes changes for your approval."
        selected={system === "zoho"}
        onSelect={onSelectZoho}
      />
      <BackendCard
        title="Tally Prime"
        description="Zournal connects through the Tally connector on this client's computer. The company must be open in Tally Prime."
        selected={system === "tally"}
        onSelect={onSelectTally}
      />
    </div>
  );
}

export function BooksQuestionsStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "books-questions");

  if (!client) return null;

  function selectSystem(system: "zoho" | "tally") {
    updateClient(clientId, {
      backend: { system, status: "active" },
      intakeMode: "fetch",
    });
  }

  function selectBookkeepingType(value: string) {
    const bookkeepingType = value as BookkeepingType;
    if (bookkeepingType === "catch-up") {
      updateClient(clientId, { bookkeepingType, accountStartDate: priorYearDate() });
    } else {
      updateClient(clientId, { bookkeepingType });
    }
    syncCatchUpTrainingAnswer(bookkeepingType);
  }

  // The go-live "Catch-up & training" step only shows for books with a past
  // (existing Zoho / catch-up — see steps.ts). Everyone else never sees it,
  // so the answer it would have recorded is written here the moment the type
  // is chosen: nothing outstanding, start fresh. Overwriting is right on the
  // way OUT of existing books (a "learn from past entries" choice is wrong
  // for a new organisation); on the way back IN, a defaulted record is
  // cleared so the step asks its questions instead of arriving pre-answered.
  function syncCatchUpTrainingAnswer(bookkeepingType: BookkeepingType) {
    const hasPast = bookkeepingType === "existing-zoho-current" || bookkeepingType === "catch-up";
    if (!hasPast) {
      void persistAnswer<CatchUpTrainingAnswer>(clientId, CATCH_UP_TRAINING_ANSWER_KEY, {
        outstandingConfirmed: true,
        training: "fresh",
        defaulted: true,
      });
      return;
    }
    const stored = getAnswer<CatchUpTrainingAnswer>(clientId, CATCH_UP_TRAINING_ANSWER_KEY);
    if (stored?.defaulted) {
      void persistAnswer<CatchUpTrainingAnswer>(clientId, CATCH_UP_TRAINING_ANSWER_KEY, {});
    }
  }

  const isZoho = client.backend.system === "zoho";
  const isTally = client.backend.system === "tally";
  const showBookkeepingType = (isZoho || isTally) && wantsBooksConnection(client);
  const showNewBooksChecklist = isZoho && showBookkeepingType && client.bookkeepingType === "new-zoho-opening-balances";
  const showExistingBooksNote = isZoho && showBookkeepingType && client.bookkeepingType === "existing-zoho-current";
  const tallyChecklist =
    isTally && showBookkeepingType && client.bookkeepingType === "new-zoho-opening-balances"
      ? tallySetupChecklist(client.name)
      : null;

  // Filtered rather than deleted, so re-enabling the flag is a one-line change.
  // A draft saved before the flag went off keeps its stored "catch-up" — the
  // card is re-added for that client alone, because dropping it would leave a
  // selection the operator can see the effect of but can no longer change.
  const typeOptions = isTally ? TALLY_BOOKKEEPING_TYPE_OPTIONS : BOOKKEEPING_TYPE_OPTIONS;
  const bookkeepingTypeOptions =
    SHOW_CATCHUP_BOOKKEEPING || client.bookkeepingType === "catch-up"
      ? typeOptions
      : typeOptions.filter((option) => option.value !== "catch-up");

  const canContinue = (isZoho || isTally) && (!showBookkeepingType || client.bookkeepingType !== undefined);

  return (
    <SubStepShell
      title="Connect Your Accounting Software"
      description={`Choose how ${client.name || "this client"}'s books connect to Zournal.`}
      thenWhat="Continue → we'll set this client's account start date."
      onContinue={() => void goNext()}
      continueDisabled={!canContinue}
    >
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">Which accounting software does your company use?</span>
        <BackendChoice
          system={client.backend.system}
          onSelectZoho={() => selectSystem("zoho")}
          onSelectTally={() => selectSystem("tally")}
        />
      </div>

      {showBookkeepingType && (
        <QuestionCard
          headline="How will bookkeeping start for this client?"
          whyLine="This decides whether we work in existing books, set up new ones, or start fresh."
          options={bookkeepingTypeOptions}
          selected={client.bookkeepingType}
          onSelect={selectBookkeepingType}
        />
      )}

      {showExistingBooksNote && (
        <Callout variant="warning">
          Before the bank feed syncs, all existing bank transactions must be matched. This prevents
          duplicate entries.
        </Callout>
      )}

      {showNewBooksChecklist && (
        <SetupChecklist title="What setting up the new books involves" items={NEW_BOOKS_CHECKLIST} />
      )}

      {tallyChecklist && (
        <SetupChecklist title="What setting up the new books involves" items={tallyChecklist} />
      )}

    </SubStepShell>
  );
}
